import type { OwnerKind } from "@coach/shared";
import type { LocalDb } from "@coach/sync";
import {
  emptyNoteSnapshot,
  type NoteSnapshot,
  parseNoteSnapshot,
} from "../features/notes/snapshot.js";
import {
  type BoardRow,
  type BoardSummary,
  createBoard,
  deleteBoard,
  getBoard,
  listBoards,
  summarize as summarizeBoard,
  updateBoard,
} from "./boards.js";

/**
 * Notes live in the same `board` table as tactics + whiteboards, distinguished
 * by `kind='note'`. This module is a thin wrapper around the generic board
 * repo that pins the `kind` and parses the note-shaped snapshot.
 */

export interface NoteSummary
  extends Omit<BoardSummary, "fieldKind" | "orientation" | "frameCount"> {
  /** Plain-text preview captured at save time; powers the overview card. */
  preview: string | null;
}

export function summarizeNote(row: BoardRow): NoteSummary {
  const snap = parseNoteSnapshot(row.snapshot_json);
  const base = summarizeBoard(row);
  return {
    id: base.id,
    name: base.name,
    updatedAt: base.updatedAt,
    cover: null,
    preview: snap.preview ?? null,
  };
}

export function listNotes(
  db: LocalDb,
  orgId: string,
  ownerId: string,
  ownerKind: OwnerKind = "coach",
): Promise<BoardRow[]> {
  return listBoards(db, orgId, ownerId, ownerKind, "note");
}

export interface CreateNoteInput {
  orgId: string;
  ownerId: string;
  ownerKind?: OwnerKind;
  name: string;
  snapshot?: NoteSnapshot;
}

export function createNote(db: LocalDb, input: CreateNoteInput): Promise<BoardRow> {
  return createBoard(db, {
    orgId: input.orgId,
    ownerId: input.ownerId,
    ownerKind: input.ownerKind,
    kind: "note",
    name: input.name,
    snapshot: input.snapshot ?? emptyNoteSnapshot(),
  });
}

export interface UpdateNoteInput {
  id: string;
  name?: string;
  snapshot?: NoteSnapshot;
}

export function updateNote(db: LocalDb, input: UpdateNoteInput): Promise<void> {
  return updateBoard(db, {
    id: input.id,
    name: input.name,
    snapshot: input.snapshot,
  });
}

export function deleteNote(db: LocalDb, args: { id: string; orgId: string }): Promise<void> {
  return deleteBoard(db, args);
}

export function getNote(db: LocalDb, id: string): Promise<BoardRow | null> {
  return getBoard(db, id);
}
